<template>
  <el-card class="menu-page">
    <div slot="header" class="toolbar">
      <span>菜单管理</span>
      <div class="toolbar-actions">
        <el-button
          v-if="$hasPermission('sys:menu:save')"
          type="primary"
          size="mini"
          @click="openCreateRoot"
        >
          新增根菜单
        </el-button>
        <el-button size="mini" @click="load">刷新</el-button>
      </div>
    </div>

    <div class="menu-layout">
      <section class="tree-panel">
        <div class="tree-toolbar">
          <el-input
            v-model.trim="keyword"
            placeholder="搜索名称、路由、权限"
            clearable
            @input="handleFilter"
          />
        </div>

        <div class="tree-container">
          <el-tree
            ref="menuTree"
            :data="treeData"
            node-key="menuId"
            :props="{ label: 'name', children: 'children' }"
            :expand-on-click-node="false"
            :highlight-current="true"
            :filter-node-method="filterNode"
            @node-click="handleNodeClick"
          >
            <div slot-scope="{ data }" class="tree-node" :title="treeNodeTitle(data)">
              <span class="tree-node__name">{{ data.name }}</span>
              <el-tag size="mini" :type="typeTagType(data.type)" class="tree-node__tag">
                {{ typeText(data.type) }}
              </el-tag>
            </div>
          </el-tree>
        </div>
      </section>

      <section class="detail-panel">
        <template v-if="currentNode">
          <div class="detail-header">
            <div class="detail-title">
              <h3>{{ currentNode.name }}</h3>
              <p>{{ typeText(currentNode.type) }} · 上级ID：{{ currentNode.parentId || 0 }}</p>
            </div>

            <div class="detail-actions">
              <el-button
                v-if="$hasPermission('sys:menu:save')"
                type="primary"
                size="mini"
                @click="openCreateChild"
              >
                新增下级
              </el-button>
              <el-button
                v-if="$hasPermission('sys:menu:update')"
                size="mini"
                @click="openEdit(currentNode)"
              >
                菜单编辑
              </el-button>
              <el-button
                v-if="$hasPermission('sys:menu:delete')"
                type="danger"
                size="mini"
                @click="remove(currentNode)"
              >
                菜单删除
              </el-button>
            </div>
          </div>

          <el-descriptions :column="2" border class="detail-card">
            <el-descriptions-item label="菜单ID">{{ currentNode.menuId }}</el-descriptions-item>
            <el-descriptions-item label="上级ID">{{ currentNode.parentId || 0 }}</el-descriptions-item>
            <el-descriptions-item label="名称">{{ currentNode.name || '-' }}</el-descriptions-item>
            <el-descriptions-item label="类型">{{ typeText(currentNode.type) }}</el-descriptions-item>
            <el-descriptions-item label="路由地址">{{ currentNode.url || '-' }}</el-descriptions-item>
            <el-descriptions-item label="权限标识">{{ currentNode.perms || '-' }}</el-descriptions-item>
            <el-descriptions-item label="图标">{{ currentNode.icon || '-' }}</el-descriptions-item>
            <el-descriptions-item label="排序">{{ currentNode.orderNum }}</el-descriptions-item>
          </el-descriptions>
        </template>

        <div v-else class="detail-empty">
          <i class="el-icon-menu"></i>
          <p>请选择左侧菜单节点查看详情</p>
        </div>
      </section>
    </div>

    <el-dialog :title="form.menuId ? '编辑菜单' : '新增菜单'" :visible.sync="dialogVisible" width="560px">
      <el-form ref="menuForm" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="上级ID" prop="parentId">
          <el-input v-model.number="form.parentId" />
        </el-form-item>
        <el-form-item label="名称" prop="name">
          <el-input v-model.trim="form.name" />
        </el-form-item>
        <el-form-item label="路由地址" prop="url">
          <el-input v-model.trim="form.url" />
        </el-form-item>
        <el-form-item label="权限标识" prop="perms">
          <el-input v-model.trim="form.perms" />
        </el-form-item>
        <el-form-item label="类型" prop="type">
          <el-select v-model="form.type" style="width: 100%">
            <el-option :value="0" label="目录" />
            <el-option :value="1" label="菜单" />
            <el-option :value="2" label="按钮" />
          </el-select>
        </el-form-item>
        <el-form-item label="图标" prop="icon">
          <el-input v-model.trim="form.icon" />
        </el-form-item>
        <el-form-item label="排序" prop="orderNum">
          <el-input v-model.number="form.orderNum" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button
          v-if="$hasPermission(form.menuId ? 'sys:menu:update' : 'sys:menu:save')"
          type="primary"
          @click="save"
        >
          保存
        </el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import http from '../../utils/http'

function defaultForm () {
  return {
    menuId: null,
    parentId: 0,
    name: '',
    url: '',
    perms: '',
    type: 0,
    icon: 'menu',
    orderNum: 0
  }
}

export default {
  data () {
    return {
      keyword: '',
      list: [],
      treeData: [],
      currentNode: null,
      dialogVisible: false,
      form: defaultForm(),
      rules: {
        parentId: [{ required: true, message: '请输入上级ID', trigger: 'blur' }],
        name: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        type: [{ required: true, message: '请选择类型', trigger: 'change' }],
        orderNum: [{ required: true, message: '请输入排序', trigger: 'blur' }]
      }
    }
  },
  created () {
    this.load()
  },
  methods: {
    buildTree (list) {
      const sortedList = [...list].sort((a, b) => {
        const parentCompare = Number(a.parentId || 0) - Number(b.parentId || 0)
        if (parentCompare !== 0) {
          return parentCompare
        }
        const orderCompare = Number(a.orderNum || 0) - Number(b.orderNum || 0)
        if (orderCompare !== 0) {
          return orderCompare
        }
        return Number(a.menuId || 0) - Number(b.menuId || 0)
      })

      const map = {}
      const roots = []
      sortedList.forEach(item => {
        map[item.menuId] = { ...item, children: [] }
      })

      Object.values(map).forEach(item => {
        if (item.parentId && map[item.parentId]) {
          map[item.parentId].children.push(item)
        } else {
          roots.push(item)
        }
      })
      return roots
    },
    async load (selectedId) {
      const { data } = await http.get('/sys/menu/list')
      this.list = data.data || []
      this.treeData = this.buildTree(this.list)

      this.$nextTick(() => {
        if (this.$refs.menuTree) {
          this.$refs.menuTree.filter(this.keyword)
        }

        const targetId = selectedId || (this.currentNode && this.currentNode.menuId)
        if (targetId) {
          const targetNode = this.list.find(item => Number(item.menuId) === Number(targetId))
          if (targetNode) {
            this.currentNode = { ...targetNode }
            if (this.$refs.menuTree) {
              this.$refs.menuTree.setCurrentKey(targetNode.menuId)
            }
            return
          }
        }

        const firstNode = this.list[0]
        if (firstNode) {
          this.currentNode = { ...firstNode }
          if (this.$refs.menuTree) {
            this.$refs.menuTree.setCurrentKey(firstNode.menuId)
          }
        } else {
          this.currentNode = null
        }
      })
    },
    handleFilter () {
      if (this.$refs.menuTree) {
        this.$refs.menuTree.filter(this.keyword)
      }
    },
    filterNode (value, data) {
      if (!value) {
        return true
      }
      const keyword = value.toLowerCase()
      const text = `${data.name || ''} ${data.url || ''} ${data.perms || ''}`.toLowerCase()
      return text.includes(keyword)
    },
    handleNodeClick (data) {
      this.currentNode = { ...data }
    },
    treeNodeTitle (data) {
      return [data.name, data.perms].filter(Boolean).join(' / ')
    },
    typeText (type) {
      if (Number(type) === 0) return '目录'
      if (Number(type) === 1) return '菜单'
      return '按钮'
    },
    typeTagType (type) {
      if (Number(type) === 0) return ''
      if (Number(type) === 1) return 'success'
      return 'warning'
    },
    openCreateRoot () {
      this.form = defaultForm()
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.menuForm && this.$refs.menuForm.clearValidate()
      })
    },
    openCreateChild () {
      if (!this.currentNode) {
        this.$message.warning('请先选择父级菜单')
        return
      }
      if (Number(this.currentNode.type) === 2) {
        this.$message.warning('按钮节点下不能继续新增下级')
        return
      }

      this.form = defaultForm()
      this.form.parentId = this.currentNode.menuId
      this.form.type = Number(this.currentNode.type) === 0 ? 1 : 2
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.menuForm && this.$refs.menuForm.clearValidate()
      })
    },
    openEdit (row) {
      this.form = {
        menuId: row.menuId,
        parentId: Number(row.parentId || 0),
        name: row.name || '',
        url: row.url || '',
        perms: row.perms || '',
        type: Number(row.type),
        icon: row.icon || 'menu',
        orderNum: Number(row.orderNum || 0)
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.menuForm && this.$refs.menuForm.clearValidate()
      })
    },
    save () {
      this.$refs.menuForm.validate(async valid => {
        if (!valid) return
        const url = this.form.menuId ? '/sys/menu/update' : '/sys/menu/save'
        const { data } = await http.post(url, this.form)
        if (data.code === 0) {
          this.$message.success(this.form.menuId ? '菜单修改成功' : '菜单新增成功')
          const selectedId = this.form.menuId || this.currentNode?.menuId || this.form.parentId
          this.dialogVisible = false
          this.load(selectedId)
        }
      })
    },
    remove (row) {
      this.$confirm(`确定删除菜单“${row.name}”吗？`, '提示', { type: 'warning' }).then(async () => {
        const { data } = await http.post('/sys/menu/delete', [row.menuId])
        if (data.code === 0) {
          this.$message.success('菜单删除成功')
          this.currentNode = null
          this.load()
        }
      })
    }
  }
}
</script>

<style scoped>
.menu-page {
  min-height: calc(100vh - 160px);
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
}

.menu-layout {
  display: flex;
  gap: 16px;
  min-height: 620px;
}

.tree-panel {
  width: 360px;
  min-width: 360px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.tree-toolbar {
  padding: 16px;
  border-bottom: 1px solid #eef2f7;
}

.tree-container {
  flex: 1;
  overflow: auto;
  padding: 10px 12px 16px;
}

.tree-container ::v-deep .el-tree-node__content {
  height: auto;
  min-height: 36px;
  padding-right: 8px;
}

.tree-node {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-width: 0;
  padding: 4px 0;
}

.tree-node__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1f2937;
}

.tree-node__tag {
  flex-shrink: 0;
}

.detail-panel {
  flex: 1;
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 18px 18px 22px;
}

.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.detail-title h3 {
  margin: 0 0 8px;
  color: #111827;
}

.detail-title p {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
}

.detail-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.detail-card {
  margin-top: 8px;
}

.detail-empty {
  height: 100%;
  min-height: 560px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #9ca3af;
  gap: 12px;
}

.detail-empty i {
  font-size: 42px;
}
</style>
